/**
 * Ce que l'écran du code dit d'un refus de l'api (`confirm-code`,
 * `resend-code`, et le premier envoi du code par `POST …/site/bookings`).
 * Pur, sans I/O ni React. Les codes sont ceux du contrat de FIXERLIGHT-056,
 * lot 2 (`docs/specs/fixerlight-056-site-du-reparateur.md` de l'api, § 2.5 et
 * § 2.6).
 *
 * Retourne :
 * - `{ creneauPris: true }` : le code est bon mais l'horaire a été pris entre-
 *   temps. L'api transforme la demande en demande ordinaire à modérer ;
 * - `{ sansCode: true }` : la demande est enregistrée, mais plus aucun code ne
 *   la confirmera (le courriel du code a échoué pour de bon, l'api l'a remise
 *   à modérer, FIXERLIGHT-807). Ce n'est pas une erreur : l'écran « demande
 *   envoyée » le dit ;
 * - `{ message, attente? }` : de quoi le dire, et le délai (secondes) avant de
 *   pouvoir redemander un code quand l'api le donne.
 *
 * `contexte` dit ce que l'écran sait et que l'api ne dit pas :
 * - `renvoi` : le refus vient de `resend-code`. Un `booking_already_decided`
 *   y veut dire que la demande n'attend plus de code ;
 * - `codeRecent` : le dernier code est parti il y a moins que sa durée de vie.
 *   Un `code_expired` ne peut alors pas être une expiration : le code a été
 *   effacé, la demande est à modérer.
 */

/** La durée pendant laquelle l'api refuse un renvoi après un envoi. */
export const DELAI_RENVOI_S = 60;

/**
 * Un code vit 10 minutes côté api. Une minute de marge : entre 9 et 10 minutes,
 * l'écran ne tranche pas et laisse le client redemander un code.
 */
export const CODE_RECENT_S = 540;

export function lireRefusCode(error, contexte = {}) {
  const status = error && error.status;
  const code = error && error.code;
  const details = (error && error.details) || {};

  if (code === 'slot_taken' || status === 409) return { creneauPris: true };

  switch (code) {
    case 'code_invalid': {
      const n = details.attempts_left;
      return {
        message:
          typeof n === 'number'
            ? `Ce code n’est pas le bon. Il vous reste ${n} essai${
                n > 1 ? 's' : ''
              }.`
            : 'Ce code n’est pas le bon.',
      };
    }
    case 'code_expired':
      if (contexte.codeRecent) return { sansCode: true };
      return { message: 'Ce code a expiré. Demandez-en un nouveau.' };
    case 'code_locked':
      return {
        message: 'Trop d’essais avec ce code. Demandez-en un nouveau.',
      };
    case 'booking_already_decided':
      if (contexte.renvoi) return { sansCode: true };
      return {
        message:
          'Cette demande a déjà été traitée. Regardez vos emails : la réponse de l’atelier y est peut-être.',
      };
    case 'code_resend_too_soon':
      return {
        message:
          'Un code vient de partir. Patientez un instant avant d’en demander un autre.',
        attente: Math.ceil(Number(details.retry_after_seconds)) || undefined,
      };
    case 'code_rate_limited_email':
      return {
        message:
          'Trop de codes ont été demandés pour cette adresse. Réessayez dans une heure.',
      };
    case 'code_mail_unavailable':
      return {
        message:
          'Le code ne peut pas être envoyé pour le moment. Envoyez une simple demande : l’atelier confirmera votre créneau.',
      };
    default:
      break;
  }

  if (status === 429) {
    return {
      message: 'Trop de demandes. Réessayez dans quelques minutes.',
    };
  }
  return {
    message:
      'La vérification n’a pas abouti. Vérifiez votre connexion et réessayez.',
  };
}
